/**
 * Encapsulates the save logic for the Schedule Interview wizard.
 * Handles phone-edit side effects, multipart vs JSON payload routing,
 * mailbox pre-flight, and error mapping for all four mutation paths
 * (create/edit × manual/self-schedule).
 */
import { useCallback } from 'react';
import { useCreateInterview, useUpdateInterview } from '@/api/useInterviews';
import {
  useCreateSelfSchedule,
  useUpdateSelfSchedule,
} from '@/api/useSelfSchedule';
import { useShowToast } from '@/hooks/useShowToast';
import { useMailBox } from '@/hooks/use-mailbox';
import { buildPayload, buildPayloadFormData } from '../payload';
import { api, ApiError } from '@/api/request';
import { getErrorMessage } from '@/utils/errors';
import { SCHEDULE_INTERVIEW_COPY } from '../config/schedule.copy';
import type {
  ScheduleMode,
  ScheduleInterviewPayload,
  PhoneValue,
} from '../config/schedule.types';

export type { PhoneValue };

function phoneValuesEqual(
  a: PhoneValue | null | undefined,
  b: PhoneValue | null | undefined
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a.dialCode ?? '').trim() === (b.dialCode ?? '').trim() &&
    (a.number ?? '').trim() === (b.number ?? '').trim()
  );
}

interface UseScheduleSaveOptions {
  mode: ScheduleMode;
  isEdit: boolean;
  isSelfSchedule: boolean;
  applicationId?: number;
  candidateId?: number;
  jobId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  scheduleId?: number;
  initialCandidatePhone?: PhoneValue;
  onClose?: () => void;
  onSuccess?: () => void;
}

interface UseScheduleSaveResult {
  handleComplete: (_payload: ScheduleInterviewPayload) => Promise<void>;
  isSaving: boolean;
}

export function useScheduleSave({
  mode,
  isEdit,
  isSelfSchedule,
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  interviewId,
  scheduleId,
  initialCandidatePhone,
  onClose,
  onSuccess,
}: UseScheduleSaveOptions): UseScheduleSaveResult {
  const toast = useShowToast();
  const { isMailBoxConnected } = useMailBox();

  const createInterview = useCreateInterview();
  const updateInterview = useUpdateInterview();
  const createSelfSchedule = useCreateSelfSchedule();
  const updateSelfSchedule = useUpdateSelfSchedule();

  const isSaving =
    createInterview.isPending ||
    updateInterview.isPending ||
    createSelfSchedule.isPending ||
    updateSelfSchedule.isPending;

  const handleComplete = useCallback(
    async (payload: ScheduleInterviewPayload) => {
      const contextIds = {
        ats_application_id: applicationId,
        ats_job_id: jobId,
        ats_job_stage_id: stageId,
        ats_job_stage_action_id: stageActivityId ?? null,
        candidate_id: candidateId,
      };

      // Pre-flight: CORE-4040 `validate_comm_email_senders` rejects the
      // save when send_email_to_* is on but the sender's mailbox isn't
      // connected. Check client-side so the user doesn't lose what they typed.
      const comm = (payload.communication ?? {}) as {
        send_email_to_candidate?: boolean;
        send_email_to_interviewer?: boolean;
      };
      const wantsToSendEmail =
        comm.send_email_to_candidate === true ||
        comm.send_email_to_interviewer === true;
      if (wantsToSendEmail && !isMailBoxConnected) {
        toast({ title: 'Email not connected', status: 'error' });
        return;
      }

      // /ats_interviews wants multipart so attachments can carry File blobs.
      // /ats_schedules does its own convertToFormData inside useSelfSchedule.
      const interviewFormData = buildPayloadFormData(payload, contextIds, mode);
      const selfScheduleJson = buildPayload(payload, contextIds, mode);

      try {
        // Phone edit → contact update. Only fires when the user actually
        // opened the pencil AND the new value differs from initialCandidatePhone.
        const editedPhone = payload.details.candidate_phone as
          | PhoneValue
          | null
          | undefined;
        const phoneWasEdited =
          payload.details.isCandidatePhoneEditing === 'edit' &&
          !phoneValuesEqual(editedPhone, initialCandidatePhone);
        if (
          phoneWasEdited &&
          typeof candidateId === 'number' &&
          candidateId > 0 &&
          editedPhone?.number
        ) {
          await api.put(
            `/contacts/${candidateId}`,
            {
              phone_code: editedPhone.dialCode ?? '',
              phone_number: editedPhone.number ?? '',
            },
            { client: 'api' }
          );
        }

        if (isSelfSchedule) {
          if (isEdit && scheduleId) {
            await updateSelfSchedule.mutateAsync({
              scheduleId,
              payload: selfScheduleJson,
            });
          } else {
            await createSelfSchedule.mutateAsync({ payload: selfScheduleJson });
          }
        } else {
          if (isEdit && interviewId) {
            await updateInterview.mutateAsync({
              interviewId,
              payload: interviewFormData,
            });
          } else {
            await createInterview.mutateAsync({ payload: interviewFormData });
          }
        }

        // Self-schedule edit: the outer wrapper (SelfSchedule.tsx) fires
        // its own toast in onSuccess — skip ours to avoid showing two.
        const outerHandlesToast = isSelfSchedule && isEdit;
        if (!outerHandlesToast) {
          toast({
            title: isEdit
              ? SCHEDULE_INTERVIEW_COPY.toast.updateSuccess.title
              : SCHEDULE_INTERVIEW_COPY.toast.createSuccess.title,
            status: 'success',
          });
        }
        onSuccess?.();
        onClose?.();
      } catch (err) {
        const errors =
          err instanceof ApiError && err.errors.length > 0
            ? err.errors
            : undefined;
        const description = errors
          ? getErrorMessage({ errors }, 'scheduler')
          : SCHEDULE_INTERVIEW_COPY.toast.saveError.fallback;
        toast({
          title: SCHEDULE_INTERVIEW_COPY.toast.saveError.title,
          description,
          status: 'error',
        });
      }
    },
    [
      mode,
      applicationId,
      candidateId,
      initialCandidatePhone,
      isMailBoxConnected,
      jobId,
      stageId,
      stageActivityId,
      createInterview,
      createSelfSchedule,
      interviewId,
      isEdit,
      isSelfSchedule,
      onClose,
      onSuccess,
      scheduleId,
      toast,
      updateInterview,
      updateSelfSchedule,
    ]
  );

  return { handleComplete, isSaving };
}
